import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { usePermission } from '../../../context/PermissionContext';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import PermissionGuard from '../../../components/common/PermissionGuard';
import { projectApi } from '../../../api/projectApi';
import { journalApi } from '../../../api/journalApi';
import '../../../components/tools/journal/SiteJournal.css';
import ProjectList from '../../../components/projects/ProjectList';
import ProjectDetailModal from '../../../components/projects/ProjectModal/ProjectDetailModal';
import AttachmentUploader from '../../../components/tools/journal/AttachmentUploader';
import ExportModal from '../../../components/tools/journal/ExportModal';

const PRIORITY_OPTIONS = [
    { value: 'critical', label: 'Kritisch', color: '#7f1d1d' },
    { value: 'high', label: 'Hoch', color: '#ef4444' },
    { value: 'medium', label: 'Mittel', color: '#f59e0b' },
    { value: 'low', label: 'Niedrig', color: '#94a3b8' }
];

const SORT_OPTIONS = [
    { value: 'date_desc', label: 'Datum (Neu -> Alt)' },
    { value: 'date_asc', label: 'Datum (Alt -> Neu)' },
    { value: 'priority_desc', label: 'Prio (Hoch -> Niedrig)' },
    { value: 'priority_asc', label: 'Prio (Niedrig -> Hoch)' }
];

const WEATHER_CODES = {
    0: 'Klarer Himmel',
    1: 'Überwiegend klar', 2: 'Teilweise bewölkt', 3: 'Bedeckt',
    45: 'Nebel', 48: 'Raureifnebel',
    51: 'Leichter Nieselregen', 53: 'Mäßiger Nieselregen', 55: 'Dichter Nieselregen',
    56: 'Leichter gefrierender Nieselregen', 57: 'Dichter gefrierender Nieselregen',
    61: 'Leichter Regen', 63: 'Mäßiger Regen', 65: 'Starker Regen',
    66: 'Leichter gefrierender Regen', 67: 'Starker gefrierender Regen',
    71: 'Leichter Schneefall', 73: 'Mäßiger Schneefall', 75: 'Starker Schneefall',
    77: 'Schneegriesel',
    80: 'Leichte Regenschauer', 81: 'Mäßige Regenschauer', 82: 'Starke Regenschauer',
    85: 'Leichte Schneeschauer', 86: 'Starke Schneeschauer',
    95: 'Gewitter',
    96: 'Gewitter mit leichtem Hagel', 99: 'Gewitter mit starkem Hagel'
};

export default function JournalPage() {
    const { setCurrentProject, can } = usePermission();
    const { user } = useAuth();
    const navigate = useNavigate();
    const { projectId } = useParams();
    const location = useLocation();


    // Helper for Entry Permissions
    const canEditEntry = (entry) => {
        if (!entry) return false;
        const isOwn = entry.user_id === user?.id;
        return can('project', 'journal.edit_others') || (isOwn && can('project', 'journal.edit_own'));
    };

    const canDeleteEntry = (entry) => {
        if (!entry) return false;
        const isOwn = entry.user_id === user?.id;
        return can('project', 'journal.delete_others') || (isOwn && can('project', 'journal.delete_own'));
    };

    const [view, setView] = useState('projects'); // 'projects' | 'list' | 'form'
    // State for ProjectList is handled internally by ProjectList for the 'projects' view
    // But we need state for managing the active project in 'list' view
    const [projects, setProjects] = useState([]);
    const [entries, setEntries] = useState([]);
    const [activeProject, setActiveProject] = useState(null);
    const [loading, setLoading] = useState(false);

    // Sync Active Project to Permission Context
    useEffect(() => {
        // If we are in 'projects' view, activeProject might be null or stale.
        // We set explicitly when activeProject changes.
        setCurrentProject(activeProject);
    }, [activeProject, setCurrentProject]);

    // Modals for "List View" management actions
    const [showProjectModal, setShowProjectModal] = useState(false);
    const [projectModalInitialTab, setProjectModalInitialTab] = useState('data');

    // Entry Detail Modal
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [showExportModal, setShowExportModal] = useState(false);

    // Bulk Selection
    const [selectedIds, setSelectedIds] = useState([]);

    // Filters
    const [searchText, setSearchText] = useState('');
    const [filterPriorities, setFilterPriorities] = useState(PRIORITY_OPTIONS.map(o => o.value));
    const [isPriorityFilterOpen, setIsPriorityFilterOpen] = useState(false);
    const priorityFilterRef = useRef(null);
    const [filterAuthors, setFilterAuthors] = useState(null);
    const [isAuthorFilterOpen, setIsAuthorFilterOpen] = useState(false);
    const authorFilterRef = useRef(null);
    const [sortConfig, setSortConfig] = useState('date_desc');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    // Form State
    const [formMode, setFormMode] = useState('create'); // 'create' | 'edit'
    const [form, setForm] = useState({
        id: null,
        date: new Date().toISOString().split('T')[0],
        time: new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }),
        title: '',
        content: '',
        weather: '',
        weather_desc: '',
        temp_max: '',
        temp_min: '',
        precipitation: '',
        problem_priority: 'low',
        assigned_to: ''
    });
    const [formError, setFormError] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const newEntryUploaderRef = useRef(null);
    const editEntryUploaderRef = useRef(null);

    // Phase 2: Form State
    const [showIntegratedForm, setShowIntegratedForm] = useState(false);
    const [expandedEntries, setExpandedEntries] = useState([]); // Array of IDs
    const [editingEntryId, setEditingEntryId] = useState(null);
    const [isFetchingWeather, setIsFetchingWeather] = useState(false);
    const [showWeatherFields, setShowWeatherFields] = useState(false);

    const token = localStorage.getItem('token');

    // Check if token exists
    if (!token) {
        console.warn("No token found");
    }

    const loadProjects = async () => {
        if (!token) return;
        try {
            const data = await projectApi.getProjects();
            setProjects(data);
        } catch (err) {
            console.error("Failed to load projects:", err);
        }
    };

    useEffect(() => {
        if (token) loadProjects();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (priorityFilterRef.current && !priorityFilterRef.current.contains(event.target)) {
                setIsPriorityFilterOpen(false);
            }
            if (authorFilterRef.current && !authorFilterRef.current.contains(event.target)) {
                setIsAuthorFilterOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Handle Escape key for Entry Detail Modal
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape' && selectedEntry) {
                setSelectedEntry(null);
            }
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [selectedEntry]);

    // Handle Deep Link
    useEffect(() => {
        if (projectId && projects.length > 0) {
            // Find full project object in loaded projects to ensure we have latest data/permissions
            const target = projects.find(p => p.id === projectId);
            if (target) {
                enterProjectInternal(target);
            }
        } else if (!projectId) {
            setView('projects');
            setActiveProject(null);
        }
    }, [projectId, projects]);

    const openProject = async (project) => {
        navigate(`/tools/journal/${project.id}`, { state: location.state });
    };

    const enterProjectInternal = async (project) => {
        setActiveProject(project);
        setView('list');
        loadEntries(project.id);
    };

    const loadEntries = async (projectId) => {
        setLoading(true);
        try {
            const data = await journalApi.getEntries(projectId);
            // Ensure status is at least an empty string or 'approved' if null for frontend consistency
            const mappedData = data.map(e => ({
                ...e,
                status: e.status || 'approved'
            }));
            setEntries(mappedData);

            // Auto-refresh the selected entry if it's currently open in the detail modal
            setSelectedEntry(prev => {
                if (!prev) return prev;
                const updated = data.find(e => e.id === prev.id);
                return updated || prev;
            });

            // Focus specific entry if requested via location state
            if (location.state?.focusEntryId) {
                const entry = mappedData.find(e => e.id === location.state.focusEntryId);
                if (entry) {
                    setSelectedEntry(entry);
                }
            }
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    };

    // Phase 2 Handlers

    const handleApprove = async (id) => {
        try {
            await journalApi.approveEntry(id);
            loadEntries(activeProject.id);
        } catch (err) {
            alert('Fehler bei Freigabe: ' + (err.response?.data?.error || 'Unbekannt'));
        }
    };

    const handleReject = async (id) => {
        const reason = window.prompt('Grund für die Ablehnung (optional):');
        if (reason === null) return; // Cancel
        try {
            await journalApi.rejectEntry(id, reason);
            loadEntries(activeProject.id);
        } catch (err) {
            alert('Fehler bei Ablehnung: ' + (err.response?.data?.error || 'Unbekannt'));
        }
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        setFormError('');
        // Date, time and content are required. title and weather are optional.
        if (!form.content || !form.content.trim()) {
            setFormError('Inhalt ist ein Pflichtfeld.');
            return;
        }
        try {
            setIsSaving(true);
            const weatherSummary = form.weather_desc
                ? `${form.weather_desc}${form.temp_max ? `, Max: ${form.temp_max}°C` : ''}${form.temp_min ? `, Min: ${form.temp_min}°C` : ''}${form.precipitation ? `, Niederschlag: ${form.precipitation}mm` : ''}`
                : form.weather;
            let appendedContent = '';
            if (formMode === 'create' && newEntryUploaderRef.current) {
                const ts = newEntryUploaderRef.current.getPendingTranscript();
                if (ts && ts.text) {
                    appendedContent = `\n\nTransskript der Sprachnachricht "${ts.name}":\n${ts.text}`;
                }
            } else if (formMode === 'edit' && editEntryUploaderRef.current) {
                const ts = editEntryUploaderRef.current.getPendingTranscript();
                if (ts && ts.text) {
                    appendedContent = `\n\nTransskript der Sprachnachricht "${ts.name}":\n${ts.text}`;
                }
            }

            const dataToSubmit = {
                ...form,
                content: (form.content + appendedContent).trim(),
                weather: weatherSummary,
                weather_data: {
                    description: form.weather_desc || form.weather,
                    desc: form.weather_desc,
                    max: form.temp_max,
                    min: form.temp_min,
                    rain: form.precipitation
                }
            };

            if (formMode === 'create') {
                const res = await journalApi.createEntry({
                    ...dataToSubmit,
                    project_id: activeProject.id
                });

                // Upload deferred attachments
                if (newEntryUploaderRef.current) {
                    await newEntryUploaderRef.current.uploadPending(res.id);
                }
            } else {
                if (!window.confirm('Eintrag wirklich aktualisieren?')) {
                    setIsSaving(false);
                    return;
                }
                await journalApi.updateEntry(form.id, dataToSubmit);

                // Upload deferred attachments for edit
                if (editEntryUploaderRef.current) {
                    await editEntryUploaderRef.current.uploadPending(form.id);
                }
            }
            setShowIntegratedForm(false);
            setEditingEntryId(null);
            setView('list');
            loadEntries(activeProject.id);
            // Scroll to the updated entry or top if new
            if (formMode === 'create') window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (err) {
            alert('Fehler beim Speichern: ' + (err.response?.data?.error || 'Unbekannt'));
        } finally {
            setIsSaving(false);
        }
    };



    const handleBulkDelete = async () => {
        if (!window.confirm(`${selectedIds.length} Einträge wirklich löschen?`)) return;
        try {
            await journalApi.bulkDeleteEntries(selectedIds);
            setSelectedIds([]);
            loadEntries(activeProject.id);
        } catch (err) {
            alert('Fehler beim Löschen: ' + (err.response?.data?.error || 'Fehler'));
        }
    };

    const toggleSelection = (id, e) => {
        e.stopPropagation();

        // Determine whether clicked item is in approved (main log) or pending (drafts) list
        const isApproved = filteredEntries.some(entry => entry.id === id);

        setSelectedIds(prev => {
            if (prev.includes(id)) return prev.filter(i => i !== id);

            // If there's an existing selection, ensure we don't mix lists
            if (prev.length > 0) {
                const prevIsApproved = filteredEntries.some(entry => entry.id === prev[0]);
                if (isApproved !== prevIsApproved) {
                    // Selection is from a different list, so we reset and only select the new one
                    return [id];
                }
            }
            return [...prev, id];
        });
    };

    const handleDelete = async (entry, skipConfirm = false) => {
        if (!skipConfirm && !window.confirm('Eintrag wirklich löschen?')) return;
        try {
            await journalApi.deleteEntry(entry.id);
            loadEntries(activeProject.id);
        } catch (err) {
            alert('Fehler beim Löschen: ' + (err.response?.data?.error || 'Fehler'));
        }
    };

    // Priority label helper
    const priorityLabel = (p) => ({ low: 'Niedrig', medium: 'Mittel', high: 'Hoch', critical: 'Kritisch' }[p] || p);
    const priorityColor = (p) => ({ critical: '#7f1d1d', high: '#ef4444', medium: '#f59e0b', low: '#94a3b8' }[p] || '#94a3b8');
    const priorityBg = (p) => ({ critical: '#fee2e2', high: '#fef2f2', medium: '#fffbeb', low: '#f8fafc' }[p] || '#f8fafc');

    const priorityBorder = (p) => ({ critical: '#b91c1c', high: '#fca5a5', medium: '#fde047', low: '#e2e8f0' }[p] || '#e2e8f0');

    // Priority Value helper (Critical = 3, Low = 0)
    const getPriorityValue = (p) => ({ critical: 3, high: 2, medium: 1, low: 0 }[p] || 0);

    // Timezone-safe date parsing helper
    const parseLocalDate = (dateStr) => {
        if (!dateStr) return '';
        const d = dateStr.split('T')[0];
        const [y, m, day] = d.split('-');
        return `${day.padStart(2, '0')}.${m.padStart(2, '0')}.${y}`;
    };
    const parseLocalDateFull = (dateStr) => parseLocalDate(dateStr);
    const parseLocalDateInput = (dateStr) => {
        if (!dateStr) return '';
        return dateStr.split('T')[0];
    };

    const formatTimestamp = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }) + ' Uhr';
    };

    const formatWeatherDisplay = (entry) => {
        if (!entry) return '';
        let weatherDesc = '';
        let tempMax = '';
        let tempMin = '';
        let precipitation = '';

        if (entry.weather_data) {
            try {
                const wd = typeof entry.weather_data === 'string' ? JSON.parse(entry.weather_data) : entry.weather_data;
                if (wd && Object.keys(wd).length > 0) {
                    weatherDesc = wd.desc || wd.description || '';
                    tempMax = wd.max || wd.temp_max || '';
                    tempMin = wd.min || wd.temp_min || '';
                    precipitation = wd.rain || wd.precipitation || '';
                }
            } catch (e) { }
        }

        let stringToParse = '';
        if (weatherDesc && weatherDesc.includes('Max:')) stringToParse = weatherDesc;
        else if (!weatherDesc && entry.weather && entry.weather.includes('Max:')) stringToParse = entry.weather;

        if (stringToParse) return stringToParse;
        else if (!weatherDesc && entry.weather) weatherDesc = entry.weather;

        let parts = [];
        if (weatherDesc && weatherDesc !== 'Unbekannt') parts.push(weatherDesc);
        if (tempMax) parts.push(`Max: ${tempMax}°C`);
        if (tempMin) parts.push(`Min: ${tempMin}°C`);
        if (precipitation) parts.push(`Niederschlag: ${precipitation}mm`);

        return parts.join(', ');
    };

    const fetchWeatherData = async (lat, lon, date) => {
        if (!lat || !lon || !date) return null;
        setIsFetchingWeather(true);
        try {
            // Use forecast API for recent data (-7 to +16 days)
            const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&start_date=${date}&end_date=${date}&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_sum&timezone=auto`;
            const res = await axios.get(url);
            const daily = res.data.daily;
            if (daily && daily.time && daily.time.length > 0) {
                const code = daily.weather_code[0];
                const desc = WEATHER_CODES[code] || 'Unbekannt';
                const max = daily.temperature_2m_max[0];
                const min = daily.temperature_2m_min[0];
                const rain = daily.precipitation_sum[0];
                return {
                    description: `${desc}, Max: ${max}°C, Min: ${min}°C, Niederschlag: ${rain}mm`,
                    raw: { code, desc, max, min, rain }
                };
            }
        } catch (err) {
            console.error("Weather fetch failed:", err);
        } finally {
            setIsFetchingWeather(false);
        }
        return null;
    };

    // Auto-fetch weather when date or project changes in CREATE mode
    useEffect(() => {
        if (showIntegratedForm && formMode === 'create' && activeProject?.latitude && activeProject?.longitude && form.date) {
            const timer = setTimeout(async () => {
                const w = await fetchWeatherData(activeProject.latitude, activeProject.longitude, form.date);
                if (w) {
                    setForm(prev => ({
                        ...prev,
                        weather: w.description,
                        weather_desc: w.raw.desc,
                        temp_max: w.raw.max,
                        temp_min: w.raw.min,
                        precipitation: w.raw.rain
                    }));
                }
            }, 500); // Debounce
            return () => clearTimeout(timer);
        }
    }, [form.date, activeProject?.id, showIntegratedForm, formMode]);

    // Auto-grow textarea helper
    const autoGrow = (e) => {
        const target = e.target;
        target.style.height = 'auto';
        target.style.height = `${target.scrollHeight}px`;
    };

    // Derive unique authors
    // We use a REF or Effect to ensure we don't reset filters on every render, 
    // but filterAuthors needs to know about available authors for the "All" logic.
    const uniqueAuthors = [...new Set(entries.map(e => e.author_name))].filter(Boolean).sort();

    // Default: null means "All" (implied). [] means "None".

    const applyJournalFilters = (items) => {
        return items.filter(e => {
            // Priority Filter
            if (!filterPriorities.includes(e.problem_priority || 'low')) return false;

            // Author Filter
            if (filterAuthors !== null && !filterAuthors.includes(e.author_name)) return false;

            // Date range filter
            if (dateFrom || dateTo) {
                const entryDate = e.date ? e.date.split('T')[0] : '';
                if (dateFrom && entryDate < dateFrom) return false;
                if (dateTo && entryDate > dateTo) return false;
            }

            if (searchText) {
                const q = searchText.toLowerCase();
                const datePadded = e.date ? parseLocalDateFull(e.date) : '';
                const fields = [
                    e.title, e.content, e.weather, e.author_name,
                    datePadded,
                    e.problem_priority ? priorityLabel(e.problem_priority) : ''
                ];
                if (!fields.some(f => (f || '').toLowerCase().includes(q))) return false;
            }
            return true;
        });
    };

    // Filtered entries (Approved)
    let approvedItems = entries.filter(e => e.status === 'approved');
    let filteredApproved = applyJournalFilters(approvedItems);

    // Filtered entries (Pending/Submitted)
    let pendingItems = entries.filter(e => e.status === 'submitted' || e.status === 'rejected');
    let filteredPending = applyJournalFilters(pendingItems);

    // Sorting Helper
    const sortJournalEntries = (items) => {
        return [...items].sort((a, b) => {
            if (sortConfig === 'date_desc') {
                const dateComp = (b.date || '').localeCompare(a.date || '');
                if (dateComp !== 0) return dateComp;
                return (b.created_at || '').localeCompare(a.created_at || '');
            }
            if (sortConfig === 'date_asc') {
                const dateComp = (a.date || '').localeCompare(b.date || '');
                if (dateComp !== 0) return dateComp;
                return (a.created_at || '').localeCompare(b.created_at || '');
            }
            if (sortConfig === 'priority_desc') {
                return getPriorityValue(b.problem_priority) - getPriorityValue(a.problem_priority);
            }
            if (sortConfig === 'priority_asc') {
                return getPriorityValue(a.problem_priority) - getPriorityValue(b.problem_priority);
            }
            return 0;
        });
    };

    const filteredEntries = sortJournalEntries(filteredApproved);
    const sortedPending = sortJournalEntries(filteredPending);

    const togglePriorityFilter = (val) => {
        setFilterPriorities(prev =>
            prev.includes(val) ? prev.filter(p => p !== val) : [...prev, val]
        );
    };

    const getPriorityFilterLabel = () => {
        if (filterPriorities.length === 0) return '0 ausgewählt';
        if (filterPriorities.length === PRIORITY_OPTIONS.length) return 'Alle Prioritäten';
        if (filterPriorities.length === 1) return PRIORITY_OPTIONS.find(o => o.value === filterPriorities[0])?.label;
        return `${filterPriorities.length} ausgewählt`;
    };

    const toggleAuthorFilter = (val) => {
        setFilterAuthors(prev => {
            // If null, we are starting from "All" (uniqueAuthors)
            const current = prev === null ? uniqueAuthors : prev;
            return current.includes(val) ? current.filter(p => p !== val) : [...current, val];
        });
    };

    const getAuthorFilterLabel = () => {
        if (filterAuthors !== null && filterAuthors.length === 0) return '0 ausgewählt';
        if (filterAuthors === null || filterAuthors.length === uniqueAuthors.length) return 'Alle Personen';
        if (filterAuthors.length === 1) return filterAuthors[0];
        return `${filterAuthors.length} ausgewählt`;
    };

    const isFilterActive = searchText || dateFrom || dateTo || (filterPriorities.length < PRIORITY_OPTIONS.length) || (filterAuthors !== null && filterAuthors.length < uniqueAuthors.length);

    const resetFilters = () => {
        setSearchText('');
        setFilterPriorities(PRIORITY_OPTIONS.map(o => o.value));
        setFilterAuthors(null);
        setDateFrom('');
        setDateTo('');
        setSortConfig('date_desc');
    };

    const handleEdit = (entry) => {
        // Extract weather from weather_data JSON if available
        let weatherValue = entry.weather || '';
        let weatherDesc = '';
        let tempMax = '';
        let tempMin = '';
        let precipitation = '';

        if (entry.weather_data) {
            try {
                const wd = typeof entry.weather_data === 'string' ? JSON.parse(entry.weather_data) : entry.weather_data;
                if (wd && Object.keys(wd).length > 0) {
                    weatherValue = wd.description || entry.weather || '';
                    weatherDesc = wd.desc || wd.description || '';
                    tempMax = wd.max || wd.temp_max || '';
                    tempMin = wd.min || wd.temp_min || '';
                    precipitation = wd.rain || wd.precipitation || '';
                }
            } catch (e) {
                console.warn("Failed to parse weather_data JSON", e);
            }
        }

        // Fallback for older entries without JSON weather_data or incorrectly stringified data
        let stringToParse = '';
        if (weatherDesc && weatherDesc.includes('Max:')) {
            stringToParse = weatherDesc;
        } else if (!weatherDesc && entry.weather && entry.weather.includes('Max:')) {
            stringToParse = entry.weather;
        }

        if (stringToParse) {
            const parts = stringToParse.split(',');
            if (parts.length > 0) weatherDesc = parts[0].trim();
            for (let part of parts) {
                if (part.includes('Max:')) tempMax = part.replace('Max:', '').replace('°C', '').trim();
                if (part.includes('Min:')) tempMin = part.replace('Min:', '').replace('°C', '').trim();
                if (part.includes('Niederschlag:')) precipitation = part.replace('Niederschlag:', '').replace('mm', '').trim();
            }
        } else if (!weatherDesc && entry.weather) {
            weatherDesc = entry.weather;
        }

        setFormMode('edit');
        setFormError('');
        setForm({
            id: entry.id,
            date: parseLocalDateInput(entry.date),
            time: entry.time || new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }),
            title: entry.title || '',
            content: entry.content || '',
            weather: weatherValue,
            weather_desc: weatherDesc,
            temp_max: tempMax,
            temp_min: tempMin,
            precipitation: precipitation,
            problem_priority: entry.problem_priority || 'low',
            assigned_to: entry.assigned_to || '',
            attachments: entry.attachments || []
        });
        setEditingEntryId(entry.id);
        setShowWeatherFields(!!(weatherDesc || tempMax || tempMin || precipitation));
        setShowIntegratedForm(false);
    };


    const handleProjectUpdate = async (updatedData, mode) => {
        try {
            // We only support EDIT here, as we are already inside a project
            await projectApi.updateProject(updatedData.id, updatedData);
            // Also update status if changed
            await projectApi.updateProjectStatus(updatedData.id, updatedData.status);

            alert('✅ Projektdaten aktualisiert');
            // Update local state
            setActiveProject({ ...activeProject, ...updatedData });
            setShowProjectModal(false);
        } catch (err) {
            alert('Fehler beim Speichern');
        }
    };

    // --- VIEWS ---

    if (view === 'projects') {
        return (
        <div className="journal-page-wrapper">
            <main className="journal-main-content">
                    <div className="projects-page-wrapper" style={{ height: '100%', overflow: 'auto' }}>
                        <ProjectList variant="tool" onProjectSelect={openProject} />
                    </div>
                </main>
            </div>
        );
    }

    if (view === 'list') {
        return (
            <main className="app-content">
                    <div className="journal-container">
                        <div className="journal-page-layout-new">
                            <div className="journal-main-content-area">
                                <div className="journal-nav-header-compact" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                    <button className="btn-header-back-minimal" onClick={() => {
                                        if (location.state?.from) {
                                            navigate(location.state.from);
                                        } else {
                                            navigate('/tools/journal', { state: location.state });
                                        }
                                    }}>
                                        ← Projekte
                                    </button>
                                    <h2 style={{ margin: 0, fontSize: '1.2rem' }}>{activeProject?.name}</h2>
                                </div>

                                {/* Filter Bar */}
                                {true && (
                                    <div className="filter-bar" style={{ flexWrap: 'nowrap', gap: '10px', alignItems: 'center' }}>
                                        <input
                                            className="search-input"
                                            placeholder="🔍 Suche..."
                                            value={searchText}
                                            onChange={e => setSearchText(e.target.value)}
                                            style={{ minWidth: '120px' }}
                                        />

                                        {/* Priority Filter Custom Dropdown */}
                                        <div className="filter-dropdown-container" ref={priorityFilterRef}>
                                            <button className="filter-dropdown-btn" onClick={() => setIsPriorityFilterOpen(!isPriorityFilterOpen)} style={{ minWidth: '150px' }}>
                                                <span>{getPriorityFilterLabel()}</span><span>▼</span>
                                            </button>
                                            {isPriorityFilterOpen && (
                                                <div className="filter-dropdown-menu">
                                                    {PRIORITY_OPTIONS.map(opt => (
                                                        <div key={opt.value} className="filter-option" onClick={() => togglePriorityFilter(opt.value)}>
                                                            <input type="checkbox" checked={filterPriorities.includes(opt.value)} readOnly />
                                                            <span className="status-dot" style={{ backgroundColor: opt.color, marginRight: '5px' }}></span>
                                                            <span className="filter-label">{opt.label}</span>
                                                        </div>
                                                    ))}
                                                    <div className="filter-actions-row">
                                                        <small className="filter-action-btn action-all" onClick={() => setFilterPriorities(PRIORITY_OPTIONS.map(o => o.value))}>Alle</small>
                                                        <span style={{ margin: '0 5px' }}>|</span>
                                                        <small className="filter-action-btn action-none" onClick={() => setFilterPriorities([])}>Keine</small>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Person Filter Custom Dropdown */}
                                        {can('project', 'journal.view_others') && (
                                            <div className="filter-dropdown-container" ref={authorFilterRef}>
                                                <button className="filter-dropdown-btn" onClick={() => setIsAuthorFilterOpen(!isAuthorFilterOpen)} style={{ minWidth: '150px' }}>
                                                    <span>{getAuthorFilterLabel()}</span><span>▼</span>
                                                </button>
                                                {isAuthorFilterOpen && (
                                                    <div className="filter-dropdown-menu">
                                                        {uniqueAuthors.map(author => (
                                                            <div key={author} className="filter-option" onClick={() => toggleAuthorFilter(author)}>
                                                                <input type="checkbox" checked={filterAuthors === null || filterAuthors.includes(author)} readOnly />
                                                                <span className="filter-label">{author}</span>
                                                            </div>
                                                        ))}
                                                        <div className="filter-actions-row">
                                                            <small className="filter-action-btn action-all" onClick={() => setFilterAuthors(uniqueAuthors)}>Alle</small>
                                                            <span style={{ margin: '0 5px' }}>|</span>
                                                            <small className="filter-action-btn action-none" onClick={() => setFilterAuthors([])}>Keine</small>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                            <span style={{ fontSize: '0.78rem', color: '#6b7280', whiteSpace: 'nowrap' }}>Von</span>
                                            <input
                                                type="date"
                                                value={dateFrom}
                                                onChange={e => setDateFrom(e.target.value)}
                                                style={{ padding: '7px 8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.78rem' }}
                                            />
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                            <span style={{ fontSize: '0.78rem', color: '#6b7280', whiteSpace: 'nowrap' }}>Bis</span>
                                            <input
                                                type="date"
                                                value={dateTo}
                                                onChange={e => setDateTo(e.target.value)}
                                                style={{ padding: '7px 8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.78rem' }}
                                            />
                                        </div>

                                        {/* Sorting Dropdown */}
                                        <select
                                            value={sortConfig}
                                            onChange={e => setSortConfig(e.target.value)}
                                            style={{ padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem', background: 'white', cursor: 'pointer', width: 'auto' }}
                                        >
                                            {SORT_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                        </select>

                                        {/* Reset Button (Fixed Layout) */}
                                        <div style={{ width: 'auto', display: 'flex', justifyContent: 'center' }}>
                                            <button
                                                onClick={resetFilters}
                                                disabled={!isFilterActive}
                                                title="Filter zurücksetzen"
                                                style={{
                                                    padding: '6px 8px',
                                                    border: '1px solid #d1d5db',
                                                    borderRadius: '20px',
                                                    background: isFilterActive ? '#f3f4f6' : 'transparent',
                                                    cursor: isFilterActive ? 'pointer' : 'default',
                                                    color: isFilterActive ? '#374151' : '#e5e7eb',
                                                    height: '32px', width: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    transition: 'all 0.2s',
                                                    fontSize: '0.9rem'
                                                }}
                                            >
                                                ↺
                                            </button>
                                        </div>
                                    </div>
                                )}

                                <div className="journal-layout-grid">
                                    {/* LEFT: MAIN CONTENT */}
                                    <div className="journal-main-column">
                                        <div className="journal-main-block">
                                            {/* Integrated Creator Form for Managers (Hidden if no create permission) */}
                                            {can('project', 'journal.create') && (
                                                <div className="integrated-creator-section">
                                                    {!showIntegratedForm ? (
                                                        <button
                                                            className="btn-integrated-open"
                                                            onClick={() => {
                                                                setForm({
                                                                    date: new Date().toISOString().split('T')[0],
                                                                    time: new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }),
                                                                    type: 'entry',
                                                                    title: '',
                                                                    content: '',
                                                                    weather: '',
                                                                    weather_desc: '',
                                                                    temp_max: '',
                                                                    temp_min: '',
                                                                    precipitation: '',
                                                                    problem_priority: 'low',
                                                                    assigned_to: '',
                                                                    attachments: []
                                                                });
                                                                setFormMode('create');
                                                                setShowWeatherFields(false);
                                                                setShowIntegratedForm(true);
                                                            }}
                                                        >
                                                            <span style={{ fontSize: '1.2rem' }}>+</span> Neuer Eintrag
                                                        </button>
                                                    ) : (
                                                        <div className="integrated-form-container">
                                                            <div className="integrated-form-header">
                                                                <h4>Neuer Eintrag</h4>
                                                                <button className="btn-close-text" onClick={() => setShowIntegratedForm(false)}>Abbrechen</button>
                                                            </div>
                                                            <form onSubmit={handleSubmit} className="journal-form-integrated">
                                                                <div className="journal-form-row-top-4">
                                                                    <div className="form-group-compact">
                                                                        <label>Datum</label>
                                                                        <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} required />
                                                                    </div>
                                                                    <div className="form-group-compact">
                                                                        <label>Uhrzeit</label>
                                                                        <input type="time" value={form.time} onChange={e => setForm({ ...form, time: e.target.value })} required />
                                                                    </div>
                                                                    <div className="form-group-compact">
                                                                        <label>Betreff</label>
                                                                        <input type="text" placeholder="Betreff..." value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
                                                                    </div>
                                                                    <div className="form-group-compact">
                                                                        <label>Priorität</label>
                                                                        <select value={form.problem_priority} onChange={e => setForm({ ...form, problem_priority: e.target.value })}>
                                                                            <option value="low">Niedrig</option>
                                                                            <option value="medium">Mittel</option>
                                                                            <option value="high">Hoch</option>
                                                                            <option value="critical">Kritisch</option>
                                                                        </select>
                                                                    </div>
                                                                </div>


                                                                <div className="form-group-compact content-field-wrapper" style={{ marginBottom: '15px', position: 'relative' }}>
                                                                    <label>Inhalt</label>
                                                                    <textarea
                                                                        placeholder="Beschreibe hier deinen Fortschritt..."
                                                                        value={form.content}
                                                                        onChange={e => setForm({ ...form, content: e.target.value })}
                                                                        onInput={autoGrow}
                                                                        rows={6}
                                                                        required
                                                                        style={{ overflow: 'hidden', minHeight: '120px' }}
                                                                        onFocus={autoGrow}
                                                                    />
                                                                    <div className="compact-attachment-controls-overlay">
                                                                        {formMode === 'create' ? (
                                                                            <AttachmentUploader
                                                                                ref={newEntryUploaderRef}
                                                                                mode="create"
                                                                                entryId={null}
                                                                                token={token}
                                                                                attachments={[]}
                                                                            />
                                                                        ) : (
                                                                            <AttachmentUploader
                                                                                ref={editEntryUploaderRef}
                                                                                mode="edit"
                                                                                entryId={form.id}
                                                                                token={token}
                                                                                attachments={form.attachments || []}
                                                                                onUpdate={(eventData) => {
                                                                                    if (eventData?.type === 'rename') {
                                                                                        setForm(prev => {
                                                                                            if (!prev.content) return prev;
                                                                                            const oldText = `Transskript der Sprachnachricht "${eventData.oldName}":`;
                                                                                            const newText = `Transskript der Sprachnachricht "${eventData.newName}":`;
                                                                                            return { ...prev, content: prev.content.replace(oldText, newText) };
                                                                                        });
                                                                                    }
                                                                                    loadEntries(activeProject.id);
                                                                                }}
                                                                            />
                                                                        )}
                                                                    </div>
                                                                </div>


                                                                {showWeatherFields && (
                                                                    <div className="weather-edit-section">
                                                                        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 1.5fr) 90px 90px 150px', gap: '15px' }}>
                                                                            <div className="form-group-compact">
                                                                                <label>Wetter {isFetchingWeather && <span style={{ fontSize: '0.6rem', color: '#4f46e5' }}>(lädt...)</span>}</label>
                                                                                <input type="text" placeholder="z.B. Sonnig" value={form.weather_desc} onChange={e => setForm({ ...form, weather_desc: e.target.value })} />
                                                                            </div>
                                                                            <div className="form-group-compact">
                                                                                <label>Max [°C]</label>
                                                                                <input type="number" step="0.1" value={form.temp_max} onChange={e => setForm({ ...form, temp_max: e.target.value })} />
                                                                            </div>
                                                                            <div className="form-group-compact">
                                                                                <label>Min [°C]</label>
                                                                                <input type="number" step="0.1" value={form.temp_min} onChange={e => setForm({ ...form, temp_min: e.target.value })} />
                                                                            </div>
                                                                            <div className="form-group-compact">
                                                                                <label>Niederschlag [mm]</label>
                                                                                <input type="number" step="0.1" value={form.precipitation} onChange={e => setForm({ ...form, precipitation: e.target.value })} />
                                                                            </div>
                                                                        </div>
                                                                        <div style={{ textAlign: 'right', marginTop: '10px' }}>
                                                                            <button type="button" className="btn-close-text" onClick={() => setShowWeatherFields(false)}>Abbrechen</button>
                                                                        </div>
                                                                    </div>
                                                                )}

                                                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', alignItems: 'center', marginTop: '12px' }}>
                                                                    {!showWeatherFields && (
                                                                        <button
                                                                            type="button"
                                                                            className="btn-add-weather-reveal"
                                                                            onClick={() => setShowWeatherFields(true)}
                                                                        >
                                                                            ⛅ Wetter hinzufügen
                                                                        </button>
                                                                    )}
                                                                    <button
                                                                        type="submit"
                                                                        className="btn-primary-compact"
                                                                        disabled={!form.date || !form.time || !form.content?.trim() || isSaving}
                                                                    >
                                                                        {isSaving ? 'Speichert...' : 'Eintrag speichern'}
                                                                    </button>
                                                                </div>
                                                            </form>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {can('project', 'journal.view_log') ? (
                                                <div className="entries-list-integrated">
                                                    {loading && <p>Lade Einträge...</p>}
                                                    {!loading && approvedItems.length === 0 && <p style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>Noch keine freigegebenen Einträge vorhanden.</p>}

                                                    {!loading && filteredEntries.length === 0 && approvedItems.length > 0 && <p style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>Keine Einträge für den aktuellen Filter.</p>}
                                                    {filteredEntries.map(e => (
                                                        <div
                                                            key={e.id}
                                                            className={`journal-entry-row ${selectedIds.includes(e.id) ? 'selected' : ''} ${editingEntryId === e.id ? 'is-editing' : ''}`}
                                                            style={{ backgroundColor: priorityBg(e.problem_priority) }}
                                                            onClick={() => {
                                                                if (editingEntryId !== e.id) setSelectedEntry(e);
                                                            }}
                                                        >
                                                            <div className="entry-row-sidebar" style={{ backgroundColor: priorityColor(e.problem_priority) }}></div>

                                                            <div className="entry-row-content">
                                                                {editingEntryId === e.id ? (
                                                                    <div className="inline-edit-form">
                                                                        <div className="inline-form-header">
                                                                            <h4>Eintrag bearbeiten</h4>
                                                                            <button type="button" className="btn-close-text" onClick={() => setEditingEntryId(null)}>Abbrechen</button>
                                                                        </div>
                                                                        <form onSubmit={handleSubmit} id="inline-edit-form">
                                                                            <div className="journal-form-row-top-4">
                                                                                <div className="form-group-compact">
                                                                                    <label>Datum</label>
                                                                                    <input type="date" value={form.date} onChange={ev => setForm({ ...form, date: ev.target.value })} required />
                                                                                </div>
                                                                                <div className="form-group-compact">
                                                                                    <label>Uhrzeit</label>
                                                                                    <input type="time" value={form.time} onChange={ev => setForm({ ...form, time: ev.target.value })} required />
                                                                                </div>
                                                                                <div className="form-group-compact">
                                                                                    <label>Betreff</label>
                                                                                    <input type="text" placeholder="Betreff..." value={form.title} onChange={ev => setForm({ ...form, title: ev.target.value })} />
                                                                                </div>
                                                                                <div className="form-group-compact">
                                                                                    <label>Priorität</label>
                                                                                    <select value={form.problem_priority} onChange={ev => setForm({ ...form, problem_priority: ev.target.value })}>
                                                                                        <option value="low">Niedrig</option>
                                                                                        <option value="medium">Mittel</option>
                                                                                        <option value="high">Hoch</option>
                                                                                        <option value="critical">Kritisch</option>
                                                                                    </select>
                                                                                </div>
                                                                            </div>

                                                                            <div className="form-group-compact content-field-wrapper" style={{ marginBottom: '15px', position: 'relative' }}>
                                                                                <label>Inhalt</label>
                                                                                <textarea
                                                                                    value={form.content}
                                                                                    onChange={ev => setForm({ ...form, content: ev.target.value })}
                                                                                    onInput={autoGrow}
                                                                                    rows={6}
                                                                                    required
                                                                                    style={{ overflow: 'hidden', minHeight: '120px' }}
                                                                                    autoFocus
                                                                                    onFocus={autoGrow}
                                                                                />
                                                                                <div className="compact-attachment-controls-overlay">
                                                                                    <AttachmentUploader
                                                                                        ref={editEntryUploaderRef}
                                                                                        mode="edit"
                                                                                        entryId={e.id}
                                                                                        token={token}
                                                                                        attachments={e.attachments || []}
                                                                                        onUpdate={(eventData) => {
                                                                                            if (eventData?.type === 'rename') {
                                                                                                setForm(prev => {
                                                                                                    if (!prev.content) return prev;
                                                                                                    const oldText = `Transskript der Sprachnachricht "${eventData.oldName}":`;
                                                                                                    const newText = `Transskript der Sprachnachricht "${eventData.newName}":`;
                                                                                                    return { ...prev, content: prev.content.replace(oldText, newText) };
                                                                                                });
                                                                                            }
                                                                                            loadEntries(activeProject.id);
                                                                                        }}
                                                                                    />
                                                                                </div>
                                                                            </div>

                                                                            {showWeatherFields && (
                                                                                <div className="weather-edit-section">
                                                                                    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 1.5fr) 90px 90px 150px', gap: '15px' }}>
                                                                                        <div className="form-group-compact">
                                                                                            <label>Wetter</label>
                                                                                            <input type="text" placeholder="z.B. Sonnig" value={form.weather_desc} onChange={ev => setForm({ ...form, weather_desc: ev.target.value })} />
                                                                                        </div>
                                                                                        <div className="form-group-compact">
                                                                                            <label>Max [°C]</label>
                                                                                            <input type="number" step="0.1" value={form.temp_max} onChange={ev => setForm({ ...form, temp_max: ev.target.value })} />
                                                                                        </div>
                                                                                        <div className="form-group-compact">
                                                                                            <label>Min [°C]</label>
                                                                                            <input type="number" step="0.1" value={form.temp_min} onChange={ev => setForm({ ...form, temp_min: ev.target.value })} />
                                                                                        </div>
                                                                                        <div className="form-group-compact">
                                                                                            <label>Niederschlag [mm]</label>
                                                                                            <input type="number" step="0.1" value={form.precipitation} onChange={ev => setForm({ ...form, precipitation: ev.target.value })} />
                                                                                        </div>
                                                                                    </div>
                                                                                    <div style={{ textAlign: 'right', marginTop: '10px' }}>
                                                                                        <button type="button" className="btn-close-text" onClick={() => setShowWeatherFields(false)}>Abbrechen</button>
                                                                                    </div>
                                                                                </div>
                                                                            )}

                                                                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '12px', alignItems: 'center' }}>
                                                                                {!showWeatherFields && (
                                                                                    <button
                                                                                        type="button"
                                                                                        className="btn-add-weather-reveal"
                                                                                        onClick={() => setShowWeatherFields(true)}
                                                                                    >
                                                                                        ⛅ Wetter hinzufügen
                                                                                    </button>
                                                                                )}
                                                                                <button type="submit" className="btn-primary-compact" disabled={isSaving}>
                                                                                    {isSaving ? 'Speichert...' : 'Eintrag speichern'}
                                                                                </button>
                                                                            </div>
                                                                        </form>
                                                                    </div>
                                                                ) : (
                                                                    <>
                                                                        <div className="entry-row-header">
                                                                            <span className="entry-date">{parseLocalDate(e.date)} {e.time && <small>({e.time})</small>}</span>
                                                                            {e.title && <span className="entry-title-inline">{e.title}</span>}
                                                                            <div className="entry-priority-badge" style={{ backgroundColor: priorityBg(e.problem_priority), color: priorityColor(e.problem_priority), border: `1px solid ${priorityBorder(e.problem_priority)}` }}>
                                                                                {priorityLabel(e.problem_priority)}
                                                                            </div>
                                                                            {/* Attachment icons — always visible when entry has attachments */}
                                                                            {e.attachments?.length > 0 && (
                                                                                <AttachmentUploader
                                                                                    mode="icons"
                                                                                    attachments={e.attachments}
                                                                                />
                                                                            )}
                                                                        </div>
                                                                        <div className={`entry-text ${expandedEntries.includes(e.id) ? 'expanded' : 'collapsed'}`}>
                                                                            {e.content}
                                                                        </div>

                                                                        {e.content?.length > 300 && (
                                                                            <button
                                                                                className="read-more-btn"
                                                                                onClick={(ev) => { ev.stopPropagation(); setExpandedEntries(prev => prev.includes(e.id) ? prev.filter(id => id !== e.id) : [...prev, e.id]); }}
                                                                            >
                                                                                {expandedEntries.includes(e.id) ? '↑ Weniger' : '↓ Mehr Details'}
                                                                            </button>
                                                                        )}

                                                                        {/* Attachments: only shown when row is expanded (view mode) */}
                                                                        {expandedEntries.includes(e.id) && e.attachments?.length > 0 && (
                                                                            <div onClick={ev => ev.stopPropagation()} style={{ marginTop: '10px' }}>
                                                                                <AttachmentUploader
                                                                                    mode="view"
                                                                                    entryId={e.id}
                                                                                    token={token}
                                                                                    attachments={e.attachments || []}
                                                                                    onUpdate={() => loadEntries(activeProject.id)}
                                                                                />
                                                                            </div>
                                                                        )}

                                                                        <div className="entry-footer-new">
                                                                            <div className="author-info">
                                                                                <strong>{e.author_name}</strong>
                                                                                <span className="author-timestamp">
                                                                                    {e.updated_at && e.updated_at !== e.created_at ? (
                                                                                        <>Geändert am {formatTimestamp(e.updated_at)}</>
                                                                                    ) : (
                                                                                        <>Erstellt am {formatTimestamp(e.created_at)}</>
                                                                                    )}
                                                                                </span>
                                                                            </div>
                                                                            <div className="entry-actions-inline">
                                                                                {formatWeatherDisplay(e) && <span className="weather-badge">⛅ {formatWeatherDisplay(e)}</span>}
                                                                                {canEditEntry(e) && (
                                                                                    <button onClick={(ev) => { ev.stopPropagation(); handleEdit(e); }} className="btn-link-action">Bearbeiten</button>
                                                                                )}
                                                                                {canDeleteEntry(e) && (
                                                                                    <button onClick={(ev) => { ev.stopPropagation(); handleDelete(e); }} className="btn-link-action danger">Löschen</button>
                                                                                )}
                                                                                {canDeleteEntry(e) && (
                                                                                    <div onClick={(ev) => ev.stopPropagation()} className="entry-checkbox-wrapper">
                                                                                        <input
                                                                                            type="checkbox"
                                                                                            checked={selectedIds.includes(e.id)}
                                                                                            onChange={(ev) => toggleSelection(e.id, ev)}
                                                                                        />
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                /* SIDEBAR REPLACEMENT FOR DRAFT VIEW */
                                                <div className="draft-view-main-approval">
                                                    <div className="approval-section-large">
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', borderBottom: '2px solid #f1f5f9', paddingBottom: '12px' }}>
                                                            <div style={{ fontSize: '1.8rem' }}>📝</div>
                                                            <div>
                                                                <h3 style={{ margin: 0, color: '#334155' }}>Ihre Entwürfe & Status</h3>
                                                                <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b' }}>Hier sehen Sie Ihre heute erstellten Einträge und deren Freigabe-Status.</p>
                                                            </div>
                                                        </div>

                                                        {sortedPending.length === 0 ? (
                                                            <div style={{ padding: '60px 40px', textAlign: 'center', background: '#f8fafc', borderRadius: '12px', border: '1px dashed #e2e8f0' }}>
                                                                <p style={{ color: '#94a3b8', margin: 0 }}>
                                                                    {isFilterActive
                                                                        ? 'Keine Entwürfe für den aktuellen Filter gefunden.'
                                                                        : 'Keine aktuellen Entwürfe vorhanden. Nutzen Sie das Feld oben, um etwas einzutragen.'}
                                                                </p>
                                                            </div>
                                                        ) : (
                                                            <div className="approval-list-large" style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                                                {sortedPending.map(p => (
                                                                    <div
                                                                        key={p.id}
                                                                        className={`journal-entry-row selectable-card ${selectedEntry?.id === p.id ? 'selected' : ''}`}
                                                                        style={{ backgroundColor: priorityBg(p.problem_priority), cursor: 'pointer', marginBottom: '15px' }}
                                                                        onClick={() => setSelectedEntry(p)}
                                                                    >
                                                                        <div className="entry-row-sidebar" style={{ backgroundColor: priorityColor(p.problem_priority) }}></div>

                                                                        <div className="entry-row-content">
                                                                            {editingEntryId === p.id ? (
                                                                                <div className="inline-edit-form">
                                                                                    <div className="inline-form-header">
                                                                                        <h4>Eintrag bearbeiten</h4>
                                                                                        <button type="button" className="btn-close-text" onClick={() => setEditingEntryId(null)}>Abbrechen</button>
                                                                                    </div>
                                                                                    <div className="journal-form-row-top-4">
                                                                                        <div className="form-group-compact">
                                                                                            <label>Datum</label>
                                                                                            <input type="date" value={form.date} onChange={ev => setForm({ ...form, date: ev.target.value })} required />
                                                                                        </div>
                                                                                        <div className="form-group-compact">
                                                                                            <label>Uhrzeit</label>
                                                                                            <input type="time" value={form.time} onChange={ev => setForm({ ...form, time: ev.target.value })} required />
                                                                                        </div>
                                                                                        <div className="form-group-compact">
                                                                                            <label>Betreff</label>
                                                                                            <input type="text" placeholder="Betreff..." value={form.title} onChange={ev => setForm({ ...form, title: ev.target.value })} />
                                                                                        </div>
                                                                                        <div className="form-group-compact">
                                                                                            <label>Priorität</label>
                                                                                            <select value={form.problem_priority} onChange={ev => setForm({ ...form, problem_priority: ev.target.value })}>
                                                                                                <option value="low">Niedrig</option>
                                                                                                <option value="medium">Mittel</option>
                                                                                                <option value="high">Hoch</option>
                                                                                                <option value="critical">Kritisch</option>
                                                                                            </select>
                                                                                        </div>
                                                                                    </div>

                                                                                    <div className="form-group-compact content-field-wrapper" style={{ marginBottom: '15px', position: 'relative' }}>
                                                                                        <label>Inhalt</label>
                                                                                        <textarea
                                                                                            value={form.content}
                                                                                            onChange={ev => setForm({ ...form, content: ev.target.value })}
                                                                                            onInput={autoGrow}
                                                                                            rows={6}
                                                                                            required
                                                                                            style={{ overflow: 'hidden', minHeight: '120px' }}
                                                                                            autoFocus
                                                                                            onFocus={autoGrow}
                                                                                        />
                                                                                        <div className="compact-attachment-controls-overlay">
                                                                                            <AttachmentUploader
                                                                                                ref={editEntryUploaderRef}
                                                                                                mode="edit"
                                                                                                entryId={p.id}
                                                                                                token={token}
                                                                                                attachments={p.attachments || []}
                                                                                                onUpdate={() => loadEntries(activeProject.id)}
                                                                                            />
                                                                                        </div>
                                                                                    </div>

                                                                                    <form onSubmit={handleSubmit} id={`inline-edit-form-${p.id}`} />
                                                                                    {showWeatherFields && (
                                                                                        <div className="weather-edit-section">
                                                                                            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 1.5fr) 90px 90px 150px', gap: '15px' }}>
                                                                                                <div className="form-group-compact">
                                                                                                    <label>Wetter</label>
                                                                                                    <input type="text" placeholder="z.B. Sonnig" value={form.weather_desc} onChange={ev => setForm({ ...form, weather_desc: ev.target.value })} />
                                                                                                </div>
                                                                                                <div className="form-group-compact">
                                                                                                    <label>Max [°C]</label>
                                                                                                    <input type="number" step="0.1" value={form.temp_max} onChange={ev => setForm({ ...form, temp_max: ev.target.value })} />
                                                                                                </div>
                                                                                                <div className="form-group-compact">
                                                                                                    <label>Min [°C]</label>
                                                                                                    <input type="number" step="0.1" value={form.temp_min} onChange={ev => setForm({ ...form, temp_min: ev.target.value })} />
                                                                                                </div>
                                                                                                <div className="form-group-compact">
                                                                                                    <label>Niederschlag [mm]</label>
                                                                                                    <input type="number" step="0.1" value={form.precipitation} onChange={ev => setForm({ ...form, precipitation: ev.target.value })} />
                                                                                                </div>
                                                                                            </div>
                                                                                            <div style={{ textAlign: 'right', marginTop: '10px' }}>
                                                                                                <button type="button" className="btn-close-text" onClick={() => setShowWeatherFields(false)}>Abbrechen</button>
                                                                                            </div>
                                                                                        </div>
                                                                                    )}

                                                                                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '12px', alignItems: 'center' }}>
                                                                                        {!showWeatherFields && (
                                                                                            <button
                                                                                                type="button"
                                                                                                className="btn-add-weather-reveal"
                                                                                                onClick={() => setShowWeatherFields(true)}
                                                                                            >
                                                                                                ⛅ Wetter hinzufügen
                                                                                            </button>
                                                                                        )}
                                                                                        <button type="submit" form={`inline-edit-form-${p.id}`} className="btn-primary-compact" disabled={isSaving}>
                                                                                            {isSaving ? 'Speichert...' : 'Eintrag speichern'}
                                                                                        </button>
                                                                                    </div>
                                                                                </div>
                                                                            ) : (
                                                                                <>
                                                                                    <div className="entry-row-header">
                                                                                        <span className="entry-date">{parseLocalDate(p.date)} {p.time && <small>({p.time})</small>}</span>
                                                                                        {p.title && <span className="entry-title-inline">{p.title}</span>}
                                                                                        <div className="entry-priority-badge" style={{ backgroundColor: priorityBg(p.problem_priority), color: priorityColor(p.problem_priority), border: `1px solid ${priorityBorder(p.problem_priority)}` }}>
                                                                                            {priorityLabel(p.problem_priority)}
                                                                                        </div>
                                                                                        {/* Attachment icons — always visible when entry has attachments */}
                                                                                        {p.attachments?.length > 0 && (
                                                                                            <AttachmentUploader
                                                                                                mode="icons"
                                                                                                attachments={p.attachments}
                                                                                            />
                                                                                        )}
                                                                                    </div>

                                                                                    <div className={`entry-text ${expandedEntries.includes(p.id) ? 'expanded' : 'collapsed'}`}>
                                                                                        {p.content}
                                                                                    </div>

                                                                                    {p.content?.length > 300 && (
                                                                                        <button
                                                                                            className="read-more-btn"
                                                                                            onClick={(ev) => { ev.stopPropagation(); setExpandedEntries(prev => prev.includes(p.id) ? prev.filter(id => id !== p.id) : [...prev, p.id]); }}
                                                                                        >
                                                                                            {expandedEntries.includes(p.id) ? '↑ Weniger' : '↓ Mehr Details'}
                                                                                        </button>
                                                                                    )}

                                                                                    {/* Attachments: only shown when row is expanded (view mode) */}
                                                                                    {expandedEntries.includes(p.id) && p.attachments?.length > 0 && (
                                                                                        <div onClick={ev => ev.stopPropagation()} style={{ marginTop: '10px' }}>
                                                                                            <AttachmentUploader
                                                                                                mode="view"
                                                                                                entryId={p.id}
                                                                                                token={token}
                                                                                                attachments={p.attachments || []}
                                                                                                onUpdate={() => loadEntries(activeProject.id)}
                                                                                            />
                                                                                        </div>
                                                                                    )}

                                                                                    <div className="entry-footer-new">
                                                                                        <div className="author-info">
                                                                                            <strong>{p.author_name}</strong>
                                                                                            <span className="author-timestamp">
                                                                                                {p.updated_at && p.updated_at !== p.created_at ? (
                                                                                                    <>Geändert am {formatTimestamp(p.updated_at)}</>
                                                                                                ) : (
                                                                                                    <>Erstellt am {formatTimestamp(p.created_at)}</>
                                                                                                )}
                                                                                            </span>
                                                                                        </div>
                                                                                        <div className="entry-actions-inline">
                                                                                            {formatWeatherDisplay(p) && <span className="weather-badge">⛅ {formatWeatherDisplay(p)}</span>}
                                                                                            {canEditEntry(p) && (
                                                                                                <button onClick={(ev) => { ev.stopPropagation(); handleEdit(p); }} className="btn-link-action">Bearbeiten</button>
                                                                                            )}
                                                                                            {canDeleteEntry(p) && (
                                                                                                <button onClick={(ev) => { ev.stopPropagation(); handleDelete(p); }} className="btn-link-action danger">Löschen</button>
                                                                                            )}
                                                                                            {(can('project', 'journal.delete_others') || canDeleteEntry(p)) && (
                                                                                                <div onClick={(ev) => ev.stopPropagation()} className="entry-checkbox-wrapper">
                                                                                                    <input
                                                                                                        type="checkbox"
                                                                                                        checked={selectedIds.includes(p.id)}
                                                                                                        onChange={(ev) => toggleSelection(p.id, ev)}
                                                                                                    />
                                                                                                </div>
                                                                                            )}
                                                                                        </div>
                                                                                    </div>

                                                                                    <div style={{ marginTop: '12px', paddingTop: '10px', borderTop: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                                        <span className="status-dot" style={{ backgroundColor: p.status === 'rejected' ? '#ef4444' : '#f59e0b' }}></span>
                                                                                        <span style={{ fontSize: '0.8rem', fontWeight: 'bold', color: p.status === 'rejected' ? '#b91c1c' : '#b45309' }}>
                                                                                            {p.status === 'rejected' ? 'ABGELEHNT' : 'WARTET AUF FREIGABE'}
                                                                                        </span>
                                                                                    </div>

                                                                                    {p.status === 'rejected' && p.rejection_reason && (
                                                                                        <div style={{ fontSize: '0.8rem', color: '#7f1d1d', background: '#fef2f2', padding: '8px 12px', borderRadius: '6px', border: '1px solid #fecaca', marginTop: '10px' }}>
                                                                                            <strong>Grund für Ablehnung:</strong> {p.rejection_reason}
                                                                                        </div>
                                                                                    )}
                                                                                </>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* RIGHT: ACTIONS & APPROVAL SIDEBAR */}
                                    <div className="journal-right-panel">
                                        <div className="sticky-right-panel">
                                            <div className="management-actions-panel">
                                                <button onClick={() => setView('projects')} className="side-menu-btn back-btn-new">
                                                    <span className="icon">←</span> <span className="label">Projekte</span>
                                                </button>

                                                <button
                                                    onClick={() => {
                                                        setProjectModalInitialTab('data');
                                                        setShowProjectModal(true);
                                                    }}
                                                    className="side-menu-btn gear-btn-new"
                                                    title="Projekt bearbeiten"
                                                >
                                                    <span className="icon">⚙️</span> <span className="label">Projekt bearbeiten</span>
                                                </button>

                                                <button
                                                    onClick={() => setShowExportModal(true)}
                                                    className="side-menu-btn"
                                                    style={{ backgroundColor: '#f8fafc', color: '#334155', border: '1px solid #cbd5e1' }}
                                                    title="PDF Export"
                                                >
                                                    <span className="icon">📄</span> <span className="label">Export (PDF)</span>
                                                </button>

                                                <PermissionGuard scope="project" permission="journal.create">
                                                    <button
                                                        onClick={() => {
                                                            setForm({
                                                                date: new Date().toISOString().split('T')[0],
                                                                title: '',
                                                                content: '',
                                                                weather: '',
                                                                problem_priority: 'low',
                                                                assigned_to: ''
                                                            });
                                                            setFormMode('create');
                                                            setShowIntegratedForm(true);
                                                            setEditingEntryId(null);
                                                            window.scrollTo({ top: 0, behavior: 'smooth' });
                                                        }}
                                                        className="side-menu-btn primary-btn-new"
                                                        title="Neuer Eintrag"
                                                    >
                                                        <span className="icon">+</span> <span className="label">Neuer Eintrag</span>
                                                    </button>
                                                </PermissionGuard>

                                                {selectedIds.length > 0 && (
                                                    <div className="side-menu-bulk-new">
                                                        <button
                                                            onClick={() => setSelectedIds([])}
                                                            className="side-menu-btn neutral-btn-new"
                                                        >
                                                            <span className="label">Auswahl aufheben</span>
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                if (selectedIds.length === 0) return;
                                                                const isApproved = filteredEntries.some(entry => entry.id === selectedIds[0]);
                                                                if (isApproved) {
                                                                    setSelectedIds(filteredEntries.map(e => e.id));
                                                                } else {
                                                                    setSelectedIds(sortedPending.map(e => e.id));
                                                                }
                                                            }}
                                                            className="side-menu-btn"
                                                            style={{ backgroundColor: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1' }}
                                                        >
                                                            <span className="label">Alle auswählen</span>
                                                        </button>
                                                        <button
                                                            onClick={handleBulkDelete}
                                                            className="side-menu-btn danger-btn-new"
                                                        >
                                                            <span className="label">{selectedIds.length} löschen</span>
                                                        </button>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Sidebar Approval List (Hidden in Draft view because it's in the center) */}
                                            {can('project', 'journal.view_log') && (
                                                <div className="journal-approval-sidebar-integrated">
                                                    <h3>
                                                        Prüf-Leiste
                                                        {sortedPending.length > 0 && <span className="approval-count">{sortedPending.length}</span>}
                                                    </h3>

                                                    {sortedPending.length === 0 ? (
                                                        <p style={{ fontSize: '0.85rem', color: '#64748b', textAlign: 'center', marginTop: '20px' }}>
                                                            Keine ausstehenden Prüfungen.
                                                        </p>
                                                    ) : (
                                                        sortedPending.map(p => (
                                                            <div
                                                                key={p.id}
                                                                className="approval-card selectable-card"
                                                                onClick={() => { setSelectedEntry(p); setEditingEntryId(null); }}
                                                                style={{ cursor: 'pointer' }}
                                                            >
                                                                <div className="approval-card-header">
                                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                                        <span style={{ fontWeight: 'bold' }}>{p.author_name}</span>
                                                                        <small style={{ color: '#94a3b8' }}>{parseLocalDate(p.date)}</small>
                                                                    </div>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                                        {p.weather && <span title={p.weather} style={{ fontSize: '1rem' }}>⛅</span>}
                                                                        <span>{new Date(p.created_at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}</span>
                                                                    </div>
                                                                </div>
                                                                {p.attachments?.length > 0 && (
                                                                    <div style={{ marginBottom: '6px' }}>
                                                                        <AttachmentUploader
                                                                            mode="icons"
                                                                            attachments={p.attachments}
                                                                        />
                                                                    </div>
                                                                )}
                                                                <div className="approval-card-content">
                                                                    {p.content}
                                                                </div>
                                                                <div className="approval-actions" style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                                                                    {(can('project', 'journal.delete_others') || canDeleteEntry(p)) && (
                                                                        <div onClick={(ev) => ev.stopPropagation()} className="entry-checkbox-wrapper" style={{ marginRight: '4px' }}>
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={selectedIds.includes(p.id)}
                                                                                onChange={(ev) => toggleSelection(p.id, ev)}
                                                                            />
                                                                        </div>
                                                                    )}
                                                                    <PermissionGuard scope="project" permission="journal.approve">
                                                                        <button
                                                                            className="btn-sidebar-edit"
                                                                            onClick={(ev) => { ev.stopPropagation(); setSelectedEntry(p); handleEdit(p); }}
                                                                        >
                                                                            Bearbeiten
                                                                        </button>
                                                                        <button
                                                                            className="btn-approve"
                                                                            onClick={(ev) => { ev.stopPropagation(); handleApprove(p.id); }}
                                                                        >
                                                                            Freigeben
                                                                        </button>
                                                                        <button
                                                                            className="btn-reject"
                                                                            onClick={(ev) => { ev.stopPropagation(); handleReject(p.id); }}
                                                                        >
                                                                            Löschen
                                                                        </button>
                                                                    </PermissionGuard>
                                                                    {!can('project', 'journal.approve') && (
                                                                        <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>Warten auf Freigabe...</span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ))
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>{/* end journal-right-panel */}
                            </div>{/* end journal-layout-grid */}

                            {/* MODALS for In-Project Management */}
                            {/* Entry Detail Modal */}
                            {
                                selectedEntry && (
                                    <div className="modal-overlay" onClick={() => { setSelectedEntry(null); setEditingEntryId(null); }}>
                                        <div className="modal-content" style={{ width: '800px', maxWidth: '90vw' }} onClick={e => e.stopPropagation()}>
                                            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '15px', borderBottom: '1px solid #eee', marginBottom: '20px' }}>
                                                <h2 style={{ margin: 0, fontSize: '1.15rem', whiteSpace: 'nowrap' }}>
                                                    📋 Eintrag vom {parseLocalDate(selectedEntry.date)}
                                                </h2>
                                                <button
                                                    className="btn-close-text"
                                                    onClick={() => { setSelectedEntry(null); setEditingEntryId(null); }}
                                                >Abbrechen</button>
                                            </div>

                                            <div className="entry-detail-body" style={{ padding: 0 }}>
                                                <div
                                                    className="journal-entry-row"
                                                    style={{ backgroundColor: '#fff', margin: 0, border: 'none', boxShadow: 'none' }}
                                                >
                                                    <div className="entry-row-sidebar" style={{ backgroundColor: priorityColor(selectedEntry.problem_priority) }}></div>
                                                    <div className="entry-row-content">
                                                        {editingEntryId === selectedEntry.id ? (
                                                            <div className="inline-edit-form">
                                                                <div className="journal-form-row-top-4">
                                                                    <div className="form-group-compact">
                                                                        <label>Datum</label>
                                                                        <input type="date" value={form.date} onChange={ev => setForm({ ...form, date: ev.target.value })} required />
                                                                    </div>
                                                                    <div className="form-group-compact">
                                                                        <label>Uhrzeit</label>
                                                                        <input type="time" value={form.time} onChange={ev => setForm({ ...form, time: ev.target.value })} required />
                                                                    </div>
                                                                    <div className="form-group-compact">
                                                                        <label>Betreff</label>
                                                                        <input type="text" placeholder="Betreff..." value={form.title} onChange={ev => setForm({ ...form, title: ev.target.value })} />
                                                                    </div>
                                                                    <div className="form-group-compact">
                                                                        <label>Priorität</label>
                                                                        <select value={form.problem_priority} onChange={ev => setForm({ ...form, problem_priority: ev.target.value })}>
                                                                            <option value="low">Niedrig</option>
                                                                            <option value="medium">Mittel</option>
                                                                            <option value="high">Hoch</option>
                                                                            <option value="critical">Kritisch</option>
                                                                        </select>
                                                                    </div>
                                                                </div>

                                                                <div className="form-group-compact content-field-wrapper" style={{ marginBottom: '15px', position: 'relative' }}>
                                                                    <label>Inhalt</label>
                                                                    <textarea
                                                                        value={form.content}
                                                                        onChange={ev => setForm({ ...form, content: ev.target.value })}
                                                                        onInput={autoGrow}
                                                                        rows={6}
                                                                        required
                                                                        style={{ overflow: 'hidden', minHeight: '120px' }}
                                                                        autoFocus
                                                                        onFocus={autoGrow}
                                                                    />
                                                                    <div className="compact-attachment-controls-overlay">
                                                                        <AttachmentUploader
                                                                            ref={editEntryUploaderRef}
                                                                            mode="edit"
                                                                            entryId={selectedEntry.id}
                                                                            token={token}
                                                                            attachments={selectedEntry.attachments || []}
                                                                            onUpdate={() => loadEntries(activeProject.id)}
                                                                        />
                                                                    </div>
                                                                </div>

                                                                <form onSubmit={handleSubmit} id="inline-edit-form-modal" />
                                                                {showWeatherFields && (
                                                                    <div className="weather-edit-section">
                                                                        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 1.5fr) 90px 90px 150px', gap: '15px' }}>
                                                                            <div className="form-group-compact">
                                                                                <label>Wetter {isFetchingWeather && <span style={{ fontSize: '0.6rem', color: '#4f46e5' }}>(lädt...)</span>}</label>
                                                                                <input type="text" placeholder="z.B. Sonnig" value={form.weather_desc} onChange={ev => setForm({ ...form, weather_desc: ev.target.value })} />
                                                                            </div>
                                                                            <div className="form-group-compact">
                                                                                <label>Max [°C]</label>
                                                                                <input type="number" step="0.1" value={form.temp_max} onChange={ev => setForm({ ...form, temp_max: ev.target.value })} />
                                                                            </div>
                                                                            <div className="form-group-compact">
                                                                                <label>Min [°C]</label>
                                                                                <input type="number" step="0.1" value={form.temp_min} onChange={ev => setForm({ ...form, temp_min: ev.target.value })} />
                                                                            </div>
                                                                            <div className="form-group-compact">
                                                                                <label>Niederschlag [mm]</label>
                                                                                <input type="number" step="0.1" value={form.precipitation} onChange={ev => setForm({ ...form, precipitation: ev.target.value })} />
                                                                            </div>
                                                                        </div>
                                                                        <div style={{ textAlign: 'right', marginTop: '10px' }}>
                                                                            <button type="button" className="btn-close-text" onClick={() => setShowWeatherFields(false)}>Abbrechen</button>
                                                                        </div>
                                                                    </div>
                                                                )}

                                                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '12px', alignItems: 'center' }}>
                                                                    {!showWeatherFields && (
                                                                        <button
                                                                            type="button"
                                                                            className="btn-add-weather-reveal"
                                                                            onClick={() => setShowWeatherFields(true)}
                                                                        >
                                                                            ⛅ Wetter hinzufügen
                                                                        </button>
                                                                    )}
                                                                    <button type="submit" form="inline-edit-form-modal" className="btn-primary-compact" disabled={isSaving}>
                                                                        {isSaving ? 'Speichert...' : 'Eintrag speichern'}
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <>
                                                                <div className="entry-row-header">
                                                                    <span className="entry-date">{parseLocalDate(selectedEntry.date)} {selectedEntry.time && <small>({selectedEntry.time})</small>}</span>
                                                                    {selectedEntry.title && <span className="entry-title-inline">{selectedEntry.title}</span>}
                                                                    <div className="entry-priority-badge" style={{ backgroundColor: priorityBg(selectedEntry.problem_priority), color: priorityColor(selectedEntry.problem_priority), border: `1px solid ${priorityBorder(selectedEntry.problem_priority)}` }}>
                                                                        {priorityLabel(selectedEntry.problem_priority)}
                                                                    </div>
                                                                    {selectedEntry.attachments?.length > 0 && (
                                                                        <AttachmentUploader
                                                                            mode="icons"
                                                                            attachments={selectedEntry.attachments}
                                                                        />
                                                                    )}
                                                                </div>

                                                                <div className="entry-text expanded">
                                                                    {selectedEntry.content}
                                                                </div>

                                                                {selectedEntry.attachments?.length > 0 && (
                                                                    <div onClick={ev => ev.stopPropagation()} style={{ marginTop: '10px' }}>
                                                                        <AttachmentUploader
                                                                            mode="view"
                                                                            entryId={selectedEntry.id}
                                                                            token={token}
                                                                            attachments={selectedEntry.attachments}
                                                                            onUpdate={() => loadEntries(activeProject.id)}
                                                                        />
                                                                    </div>
                                                                )}

                                                                <div className="entry-footer-new" style={{ marginTop: '15px' }}>
                                                                    <div className="author-info">
                                                                        <strong>{selectedEntry.author_name}</strong>
                                                                        <span className="author-timestamp">
                                                                            {selectedEntry.updated_at && selectedEntry.updated_at !== selectedEntry.created_at ? (
                                                                                <>Geändert am {formatTimestamp(selectedEntry.updated_at)}</>
                                                                            ) : (
                                                                                <>Erstellt am {formatTimestamp(selectedEntry.created_at)}</>
                                                                            )}
                                                                        </span>
                                                                    </div>
                                                                    <div className="entry-actions-inline">
                                                                        {formatWeatherDisplay(selectedEntry) && <span className="weather-badge">⛅ {formatWeatherDisplay(selectedEntry)}</span>}
                                                                    </div>
                                                                </div>

                                                                <div className="modal-actions" style={{ display: 'flex', gap: '12px', marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #f1f5f9' }}>
                                                                    {canEditEntry(selectedEntry) && (
                                                                        <button
                                                                            onClick={() => {
                                                                                handleEdit(selectedEntry);
                                                                            }}
                                                                            className="btn-primary"
                                                                            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                                        >
                                                                            Bearbeiten
                                                                        </button>
                                                                    )}

                                                                    {selectedEntry.status === 'submitted' && can('project', 'journal.approve') && (
                                                                        <button
                                                                            onClick={() => {
                                                                                handleApprove(selectedEntry.id);
                                                                                setSelectedEntry(null);
                                                                            }}
                                                                            className="btn-primary"
                                                                            style={{ background: '#10b981', borderColor: '#059669', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                                        >
                                                                            Freigeben
                                                                        </button>
                                                                    )}

                                                                    {canDeleteEntry(selectedEntry) && (
                                                                        <button
                                                                            onClick={() => {
                                                                                if (window.confirm('Wirklich löschen?')) {
                                                                                    handleDelete(selectedEntry, true);
                                                                                    setSelectedEntry(null);
                                                                                }
                                                                            }}
                                                                            className="btn-danger"
                                                                            style={{ flex: 1 }}
                                                                        >
                                                                            Löschen
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )
                            }

                            {/* MODALS for In-Project Management */}
                            {
                                activeProject && (
                                    <>
                                        <ProjectDetailModal
                                            isOpen={showProjectModal}
                                            onClose={() => setShowProjectModal(false)}
                                            project={activeProject}
                                            mode="view"
                                            onSubmit={handleProjectUpdate}
                                            onDelete={async (formData) => {
                                                if (!window.confirm(`Projekt "${formData.name || activeProject?.name}" wirklich löschen? Alle Einträge werden ebenfalls gelöscht!`)) return;
                                                try {
                                                    await projectApi.deleteProject(activeProject.id);
                                                    alert('Projekt gelöscht.');
                                                    setShowProjectModal(false);
                                                    setActiveProject(null);
                                                    setView('projects');
                                                    loadProjects();
                                                } catch (err) {
                                                    alert(err.response?.data?.error || 'Fehler beim Löschen');
                                                }
                                            }}
                                            permissions={{
                                                // Pass actual permissions
                                                can_edit_project: can('project', 'settings.edit_project_data'),
                                                delete_projects: can('project', 'settings.archive_project')
                                            }}
                                            initialTab={projectModalInitialTab}
                                        />
                                        <ExportModal
                                            isOpen={showExportModal}
                                            onClose={() => setShowExportModal(false)}
                                            project={activeProject}
                                            token={token}
                                            user={user}
                                        />
                                    </>
                                )
                            }
                        </div>
                    </div>
                </main>
        );
    }

    // FORM VIEW
    return (
        <div className="journal-page-wrapper">
            <main className="journal-main-content">
                <div className="journal-container">
                    <div className="journal-nav-header">
                        <h2 style={{ margin: 0, fontSize: '1.1rem' }}>{formMode === 'create' ? 'Neuer Eintrag' : 'Eintrag bearbeiten'}</h2>
                        <button onClick={() => setView('list')} className="btn-header-back">Abbrechen</button>
                    </div>
                    <form onSubmit={handleSubmit} className="journal-form">
                        {formError && (
                            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '6px', padding: '10px 14px', marginBottom: '12px', color: '#dc2626', fontSize: '0.88rem', fontWeight: 500 }}>
                                ⚠️ {formError}
                            </div>
                        )}

                        {/* Row 1: Betreff/Titel (full width) */}
                        <div className="form-group" style={{ marginBottom: '10px' }}>
                            <label style={{ fontSize: '0.82rem', marginBottom: '3px' }}>Betreff / Titel <span style={{ color: '#dc2626' }}>*</span></label>
                            <input
                                type="text"
                                required
                                maxLength={100}
                                placeholder="Kurze Zusammenfassung (max. 100 Zeichen)"
                                value={form.title}
                                onChange={e => setForm({ ...form, title: e.target.value })}
                                style={{ padding: '7px 10px', fontSize: '0.88rem' }}
                            />
                            <div style={{ fontSize: '0.7rem', color: form.title.length > 90 ? '#dc2626' : '#9ca3af', textAlign: 'right', marginTop: '2px' }}>{form.title.length}/100</div>
                        </div>

                        {/* Row 2: Datum, Wetter, Priorität side by side */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                            <div className="form-group" style={{ marginBottom: 0 }}>
                                <label style={{ fontSize: '0.82rem', marginBottom: '3px' }}>Datum <span style={{ color: '#dc2626' }}>*</span></label>
                                <input
                                    type="date"
                                    required
                                    value={form.date}
                                    onChange={e => setForm({ ...form, date: e.target.value })}
                                    style={{ padding: '7px 10px', fontSize: '0.85rem', width: '100%', boxSizing: 'border-box' }}
                                />
                            </div>
                            <div className="form-group" style={{ marginBottom: 0 }}>
                                <label style={{ fontSize: '0.82rem', marginBottom: '3px' }}>Wetter</label>
                                <input
                                    type="text"
                                    maxLength={50}
                                    placeholder="z.B. Sonnig, 20°C"
                                    value={form.weather}
                                    onChange={e => setForm({ ...form, weather: e.target.value })}
                                    style={{ padding: '7px 10px', fontSize: '0.85rem', width: '100%', boxSizing: 'border-box' }}
                                />
                                <div style={{ fontSize: '0.7rem', color: form.weather.length > 40 ? '#dc2626' : '#9ca3af', textAlign: 'right', marginTop: '2px' }}>{form.weather.length}/50</div>
                            </div>
                            <div className="form-group" style={{ marginBottom: 0 }}>
                                <label style={{ fontSize: '0.82rem', marginBottom: '3px' }}>Priorität</label>
                                <select
                                    value={form.problem_priority}
                                    onChange={e => setForm({ ...form, problem_priority: e.target.value })}
                                    style={{ padding: '7px 10px', fontSize: '0.85rem', width: '100%', boxSizing: 'border-box' }}
                                >
                                    <option value="low">Niedrig</option>
                                    <option value="medium">Mittel</option>
                                    <option value="high">Hoch</option>
                                    <option value="critical">Kritisch</option>
                                </select>
                            </div>
                        </div>

                        {/* Row 3: Beschreibung (full width) */}
                        <div className="form-group">
                            <label style={{ fontSize: '0.82rem', marginBottom: '3px' }}>Beschreibung <span style={{ color: '#dc2626' }}>*</span></label>
                            <textarea
                                required
                                rows={6}
                                placeholder="Was wurde heute erledigt?"
                                value={form.content}
                                onChange={e => setForm({ ...form, content: e.target.value })}
                                style={{ fontSize: '0.88rem' }}
                            />
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '15px', position: 'relative' }}>
                            <span style={{ fontSize: '0.75rem', color: '#9ca3af', whiteSpace: 'nowrap', position: 'absolute', left: 0 }}><span style={{ color: '#dc2626' }}>*</span> = Pflichtfeld</span>
                            <button type="submit" className="submit-btn btn-primary" style={{ width: 'auto', padding: '8px 30px' }}>
                                {formMode === 'create' ? '💾 Speichern' : '✅ Aktualisieren'}
                            </button>
                        </div>
                    </form>
                </div>
            </main>
        </div>
    );
}
